import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import RangeSelect from '../Notifications/elements/RangeSelect';
import LabeledSelect from '../Notifications/elements/LabeledSelect';
import InfoTip from '../Notifications/elements/InfoTip';
import UserSearchPicker from '../General/UserSearchPicker';
import Stat, { rate } from './ShopMyDMStat';
import { searchShopMyDmCreators, getShopMyDmCreator, getShopMyDmSends, getShopMyDmStatus } from '../../api/shopmyDm';
import { TIPS, BLOCKED_LABELS, whyFor, bannerFor, instagramChip, isStorySend, storyTimeLabel } from './shopmyDmCopy';

const ET = 'America/New_York';
const etDay = value => new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(value));
const etTime = value => new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value));
const OUTCOMES = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'failed', label: 'Failed' },
  { value: 'received', label: 'Received' }
];
const KINDS = [
  { value: 'all', label: 'All' },
  { value: 'link', label: 'Link' },
  { value: 'collection', label: 'Collection' }
];

const searchCreators = query => searchShopMyDmCreators(query.trim()).then(data => data.creators || []);

const ShopMyDMUsers = ({ User_id, outcome, range: openRange, onOpenCreator }) => {
  const [creator, setCreator] = useState(null);
  const [log, setLog] = useState(null);
  const [range, setRange] = useState(openRange || '7d');
  const [status, setStatus] = useState(OUTCOMES.some(option => option.value === outcome) ? outcome : 'all');
  const [kind, setKind] = useState('all');
  const [page, setPage] = useState(1);
  const [shadowOn, setShadowOn] = useState(false);

  useEffect(() => {
    let current = true;
    getShopMyDmStatus()
      .then(data => current && setShadowOn(!!data.shadowOn))
      .catch(() => current && window.ALERT?.error?.('Failed to load ShopMy DM status.'));
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    if (!User_id) return undefined;
    let current = true;
    getShopMyDmCreator(User_id)
      .then(data => current && setCreator(data.creator))
      .catch(() => current && window.ALERT?.error?.('Failed to load this creator.'));
    return () => {
      current = false;
    };
  }, [User_id]);

  useEffect(() => {
    if (!User_id) return undefined;
    let current = true;
    getShopMyDmSends(User_id, { range, status, kind, page })
      .then(data => current && setLog(data))
      .catch(() => current && window.ALERT?.error?.('Failed to load this creator’s ShopMy DM log.'));
    return () => {
      current = false;
    };
  }, [User_id, range, status, kind, page]);

  const banner = creator ? bannerFor(creator) : null;
  const instagram = creator ? instagramChip(creator) : null;
  const stats = log?.stats;
  const pages = Math.max(1, Math.ceil((log?.total || 0) / (log?.pageSize || 30)));
  let lastDay = null;

  return (
    <div className='shopmy-dm-users'>
      <p className='lede'>Look up one creator: every comment the pipeline kept, whether a DM went out, and why it didn’t.</p>
      <div className='search' data-testid='shopmy-dm-search'>
        <UserSearchPicker
          selectedUsers={[]}
          onChange={([user]) => onOpenCreator(user.id)}
          searchUsers={searchCreators}
          placeholder='Search by name or username'
          hideSelectedList
        />
      </div>
      <div className='filters'>
        <RangeSelect value={range} onChange={value => { setRange(value); setPage(1); }} options={['24h', '7d', '30d', '90d']} />
        <LabeledSelect label='Outcome' value={status} onChange={value => { setStatus(value); setPage(1); }} options={OUTCOMES} />
        <LabeledSelect label='Destination' value={kind} onChange={value => { setKind(value); setPage(1); }} options={KINDS} />
      </div>
      {!creator && <div className='card empty'>Look up a creator to see their sends.</div>}
      {creator && (
        <>
          <div className='card' data-testid='shopmy-dm-creator'>
            <div className='who'>
              <div className='id'>
                <div className='name'>{creator.name}</div>
                <div className='meta'>@{creator.username} · {creator.id}</div>
                <div className='chips'>
                  <Chip off={!creator.flagOn}>{creator.flagOn ? 'Feature flag on' : 'Feature flag off'}</Chip>
                  <Chip off={!creator.switchOn}>{creator.switchOn ? 'Their switch is on' : BLOCKED_LABELS.switch}</Chip>
                  <Chip off={instagram.off}>{instagram.label}</Chip>
                </div>
              </div>
            </div>
            {banner && <div className='banner'>{banner}</div>}
          </div>
          {stats && (
            <div className='card'>
              <div className='stats'>
                <Stat value={stats.sent} label='Sent' />
                {shadowOn && <Stat value={stats.shadow} label='Shadow' tip={TIPS.shadow} />}
                <Stat value={stats.skipped} label='Skipped' tip={TIPS.skipped} />
                <Stat value={stats.failed} label='Failed' tip={TIPS.failed} />
                <Stat value={stats.clicked} label='Clicked' />
                <Stat value={rate(stats.clicked, stats.sent)} label='Click rate' />
                <span className='note'>Same range and filters as the log below</span>
              </div>
            </div>
          )}
          <div className='card' data-testid='shopmy-dm-log'>
            <div className='grid head log'>
              <span className='label'>When</span><span className='label'>Post</span><span className='label'>Destination</span>
              <span className='label'>Comment</span><span className='label'>Outcome</span><span className='label'>Why</span>
              <span className='label right'>Clicks</span>
            </div>
            {(log?.sends || []).map(send => {
              const day = etDay(send.createdAt);
              const group = day === lastDay ? null : <div key={`day-${send.id}`} className='group'>{day}</div>;
              lastDay = day;
              return (
                <React.Fragment key={send.id}>
                  {group}
                  <div className='grid log' data-testid={`shopmy-dm-row-${send.id}`}>
                    <span className='time'>{etTime(send.createdAt)}</span>
                    {postCell(send)}
                    <span className='dest'>
                      <span className={cn('name', { unset: !send.destinationName })}>{send.destinationName || 'Not picked yet'}</span>
                      {send.kind && <span className='marker'>{send.kind}</span>}
                    </span>
                    <span className='comment'>
                      <span className='commenter'>@{send.commenterUsername}</span>
                      {send.commentCleared ? <span className='cleared'>Comment text cleared after 90 days</span> : <span className='clamp'>{send.commentText}</span>}
                    </span>
                    <span>
                      <span className={cn('pill', send.status)}>{send.status}{send.status !== 'sent' && <InfoTip message={TIPS[send.status]} />}</span>
                    </span>
                    <span className='why'>{whyFor(send)}</span>
                    <span className='num'>{send.clickCount}</span>
                  </div>
                </React.Fragment>
              );
            })}
            {log && log.total === 0 && <div className='empty'>Nothing in this range matches those filters.</div>}
            {log && log.total > 0 && (
              <div className='pager'>
                <span>Showing {(page - 1) * log.pageSize + 1}–{(page - 1) * log.pageSize + log.sends.length} of {log.total}</span>
                <span className='pages'>
                  <button type='button' disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                  <button type='button' disabled={page === pages} onClick={() => setPage(page + 1)}>Next</button>
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const postCell = send => {
  if (isStorySend(send)) {
    const time = storyTimeLabel(send.expiresAt);
    return (
      <span className='post story'>
        <span className='kind'>Story</span>
        <span className='clamp'>{send.postCaption || 'No caption'}</span>
        {time && <span className='when'>{time}</span>}
      </span>
    );
  }
  const label = send.postCaption || 'Post';
  return send.permalink ? (
    <a className='post clamp' href={send.permalink} target='_blank' rel='noreferrer'>{label}</a>
  ) : (
    <span className='post clamp'>{label}</span>
  );
};

const Chip = ({ off, children }) => (
  <span className={cn('chip', { off })}>
    <span className='dot' />
    {children}
  </span>
);

Chip.propTypes = {
  off: PropTypes.bool,
  children: PropTypes.node.isRequired
};

ShopMyDMUsers.propTypes = {
  User_id: PropTypes.string,
  outcome: PropTypes.string,
  range: PropTypes.string,
  onOpenCreator: PropTypes.func.isRequired
};

export default ShopMyDMUsers;
